import DOMPurify from "dompurify";
import { Marked } from "marked";
import { DEFAULT_MARKDOWN_CLASSES, type MarkdownClasses } from "./markdownClasses";
import { createRenderer } from "./markdownRenderer";

export { DEFAULT_MARKDOWN_CLASSES, escapeHtml, type MarkdownClasses } from "./markdownClasses";

const newMarked = (classes: MarkdownClasses) => new Marked({ gfm: true, breaks: true, renderer: createRenderer(classes) });

const marked = newMarked(DEFAULT_MARKDOWN_CLASSES);
const skinned = new WeakMap<Partial<MarkdownClasses>, Marked>();

const markedFor = (overrides?: Partial<MarkdownClasses>): Marked => {
  if (!overrides) {
    return marked;
  }
  const existing = skinned.get(overrides);
  if (existing) {
    return existing;
  }
  const created = newMarked({ ...DEFAULT_MARKDOWN_CLASSES, ...overrides });
  skinned.set(overrides, created);
  return created;
};

const ALLOWED_TAGS = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "span",
  "strong",
  "em",
  "del",
  "ul",
  "ol",
  "li",
  "input",
  "a",
  "blockquote",
  "code",
  "pre",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "hr",
  "img",
  "br",
];

const ALLOWED_ATTR = ["class", "href", "title", "target", "rel", "src", "alt", "type", "checked", "disabled", "start"];

let sanitizer: typeof DOMPurify | undefined;

const getSanitizer = () => {
  if (sanitizer) {
    return sanitizer;
  }
  sanitizer = DOMPurify();
  sanitizer.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") {
      return;
    }
    const href = node.getAttribute("href") ?? "";
    if (/^https?:\/\//i.test(href)) {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  return sanitizer;
};

export const renderMarkdown = (source: string, classes?: Partial<MarkdownClasses>): string => {
  if (!source.trim()) {
    return "";
  }
  const html = markedFor(classes).parse(source, { async: false });
  return getSanitizer().sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_TAGS: ["script", "style", "iframe"],
    FORBID_ATTR: ["onerror", "onclick", "onload"],
    ALLOW_UNKNOWN_PROTOCOLS: false,
    ALLOW_DATA_ATTR: false,
  });
};

export const markdownToPlainText = (source: string): string => {
  if (!source.trim()) {
    return "";
  }
  const html = marked.parse(source, { async: false });
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
};
